import {useMemoizedSelector} from '../../../../state/hooks';
import type * as PanelTypes from '../../../../state/views/panel/types';
import {
  makeSectionMediaActualSizeSelector,
  makeSectionMediaAlwaysOriginalSelector,
  makeSectionMediaBoundingBoxConfigSelector,
  makeSectionMediaColumnCountSelector,
  makeSectionMediaCompareSettingsSelector,
  makeSectionMediaDisableVideoStreamSelector,
  makeSectionMediaFitToDimensionSelector,
  makeSectionMediaGallerySettingsSelector,
  makeSectionMediaGridSettingsSelector,
  makeSectionMediaMaxGalleryItemsSelector,
  makeSectionMediaMaxRunsSelector,
  makeSectionMediaMaxYAxisCountSelector,
  makeSectionMediaModeSelector,
  makeSectionMediaObject3DBackgroundColorSelector,
  makeSectionMediaPixelatedSelector,
  makeSectionMediaPointColorSelector,
  makeSectionMediaSegmentationMaskConfigSelector,
  makeSectionMediaSliderKeySelector,
  makeSectionMediaSliderSyncSelector,
  makeSectionMediaSnapToExistingStepSelector,
  makeSectionMediaStepStrideLengthSelector,
  makeSectionMediaUseRightHandedSystemSelector,
  makeSectionMediaVideoPlaybackSyncAutoplaySelector,
  makeSectionMediaVideoPlaybackSyncLoopSelector,
  makeSectionMediaVideoPlaybackSyncSelector,
  makeSectionMediaVideoPlayerTimeStepSelector,
} from '../../../../state/views/panelBankSectionConfig/selectors';
import {
  makeWorkspaceMediaActualSizeSelector,
  makeWorkspaceMediaAlwaysOriginalSelector,
  makeWorkspaceMediaBoundingBoxConfigSelector,
  makeWorkspaceMediaColumnCountSelector,
  makeWorkspaceMediaCompareSettingsSelector,
  makeWorkspaceMediaDisableVideoStreamSelector,
  makeWorkspaceMediaFitToDimensionSelector,
  makeWorkspaceMediaGallerySettingsSelector,
  makeWorkspaceMediaGridSettingsSelector,
  makeWorkspaceMediaMaxGalleryItemsSelector,
  makeWorkspaceMediaMaxRunsSelector,
  makeWorkspaceMediaMaxYAxisCountSelector,
  makeWorkspaceMediaModeSelector,
  makeWorkspaceMediaObject3DBackgroundColorSelector,
  makeWorkspaceMediaPixelatedSelector,
  makeWorkspaceMediaPointColorSelector,
  makeWorkspaceMediaSegmentationMaskConfigSelector,
  makeWorkspaceMediaSliderKeySelector,
  makeWorkspaceMediaSliderSyncSelector,
  makeWorkspaceMediaSnapToExistingStepSelector,
  makeWorkspaceMediaStepStrideLengthSelector,
  makeWorkspaceMediaUseRightHandedSystemSelector,
  makeWorkspaceMediaVideoPlaybackSyncSelector,
  makeWorkspaceMediaVideoPlayerTimeStepSelector,
  makeWorkspaceSettingsVideoPlaybackSyncAutoplaySelector,
  makeWorkspaceSettingsVideoPlaybackSyncLoopSelector,
} from '../../../../state/views/workspaceSettings/selectors';
import {useSingleRunModeContext} from '../../../MultiRunWorkspace/SingleRunMode';
import {type LayoutMode} from '../../../PanelMediaBrowser/common';
import {
  useMaybeWorkspaceRefsContext,
  useWorkspaceRefsContext,
} from '../../../Workspace/WorkspaceRefsContext';
import {
  makeCascadingSectionMediaActualSizeSelector,
  makeCascadingSectionMediaAlwaysOriginalSelector,
  makeCascadingSectionMediaBoundingBoxConfigSelector,
  makeCascadingSectionMediaColumnCountSelector,
  makeCascadingSectionMediaCompareSettingsSelector,
  makeCascadingSectionMediaDisableVideoStreamSelector,
  makeCascadingSectionMediaFitToDimensionSelector,
  makeCascadingSectionMediaGallerySettingsSelector,
  makeCascadingSectionMediaGridSettingsSelector,
  makeCascadingSectionMediaMaxGalleryItemsSelector,
  makeCascadingSectionMediaMaxRunsSelector,
  makeCascadingSectionMediaMaxYAxisCountSelector,
  makeCascadingSectionMediaModeSelector,
  makeCascadingSectionMediaObject3DBackgroundColorSelector,
  makeCascadingSectionMediaPixelatedSelector,
  makeCascadingSectionMediaPointColorSelector,
  makeCascadingSectionMediaSegmentationMaskConfigSelector,
  makeCascadingSectionMediaSliderKeySelector,
  makeCascadingSectionMediaSliderSyncSelector,
  makeCascadingSectionMediaSnapToExistingStepSelector,
  makeCascadingSectionMediaStepStrideLengthSelector,
  makeCascadingSectionMediaUseRightHandedSystemSelector,
  makeCascadingSectionMediaVideoPlaybackSyncAutoplaySelector,
  makeCascadingSectionMediaVideoPlaybackSyncLoopSelector,
  makeCascadingSectionMediaVideoPlaybackSyncSelector,
  makeCascadingSectionMediaVideoPlayerTimeStepSelector,
  makeMediaActualSizeDisplaySelector,
  makeMediaAlwaysOriginalDisplaySelector,
  makeMediaBoundingBoxConfigDisplaySelector,
  makeMediaColumnCountDisplaySelector,
  makeMediaCompareSettingsDisplaySelector,
  makeMediaDisableVideoStreamDisplaySelector,
  makeMediaFitToDimensionDisplaySelector,
  makeMediaGallerySettingsDisplaySelector,
  makeMediaGridSettingsDisplaySelector,
  makeMediaMaxGalleryItemsDisplaySelector,
  makeMediaMaxRunsDisplaySelector,
  makeMediaMaxYAxisCountDisplaySelector,
  makeMediaModeDisplaySelector,
  makeMediaObject3DBackgroundColorDisplaySelector,
  makeMediaPixelatedDisplaySelector,
  makeMediaPointColorDisplaySelector,
  makeMediaSegmentationMaskConfigDisplaySelector,
  makeMediaSliderKeyDisplaySelector,
  makeMediaSliderSyncDisplaySelector,
  makeMediaSnapToExistingStepDisplaySelector,
  makeMediaStepStrideLengthDisplaySelector,
  makeMediaUseRightHandedSystemDisplaySelector,
  makeMediaVideoPlaybackSyncAutoplayDisplaySelector,
  makeMediaVideoPlaybackSyncDisplaySelector,
  makeMediaVideoPlaybackSyncIsInheritedSelector,
  makeMediaVideoPlaybackSyncLoopDisplaySelector,
  makeMediaVideoPlayerTimeStepDisplaySelector,
  makePanelMediaActualSizeSelector,
  makePanelMediaAlwaysOriginalSelector,
  makePanelMediaBoundingBoxConfigSelector,
  makePanelMediaChartTitleSelector,
  makePanelMediaColumnCountSelector,
  makePanelMediaCompareSettingsSelector,
  makePanelMediaDisableVideoStreamSelector,
  makePanelMediaFitToDimensionSelector,
  makePanelMediaGallerySettingsSelector,
  makePanelMediaGridSettingsSelector,
  makePanelMediaKeysSelector,
  makePanelMediaMaxGalleryItemsSelector,
  makePanelMediaMaxRunsSelector,
  makePanelMediaMaxYAxisCountSelector,
  makePanelMediaModeSelector,
  makePanelMediaObject3DBackgroundColorSelector,
  makePanelMediaPixelatedSelector,
  makePanelMediaPointColorSelector,
  makePanelMediaSegmentationMaskConfigSelector,
  makePanelMediaSliderKeySelector,
  makePanelMediaSnapToExistingStepSelector,
  makePanelMediaStepStrideLengthSelector,
  makePanelMediaUseRightHandedSystemSelector,
  makePanelMediaVideoPlaybackSyncAutoplaySelector,
  makePanelMediaVideoPlaybackSyncLoopSelector,
  makePanelMediaVideoPlaybackSyncSelector,
  makePanelMediaVideoPlayerTimeStepSelector,
} from '../media/mediaSettingsSelectors';
import {SettingsLevel} from '../media/types';
import {
  useSectionSettingsContext,
  useSectionSettingsContextMaybe,
} from '../SectionSettingsContextProvider';

/**
 * Raw panel-level chartTitle from the panel's own config.
 * chartTitle is a panel-only setting: it never cascades from workspace or section.
 */
export function usePanelMediaChartTitle(panelRef: PanelTypes.Ref | undefined) {
  return useMemoizedSelector(makePanelMediaChartTitleSelector, panelRef);
}

/**
 * Whether to show the reset-to-parent button for chartTitle at the current
 * settings level.
 * chartTitle is panel-only, so this is only ever true at the panel level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export function useShouldShowMediaChartTitleResetAtLevel(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): boolean {
  const panelChartTitle = usePanelMediaChartTitle(panelRef);
  return settingsLevel === SettingsLevel.PANEL && panelChartTitle != null;
}

/**
 * Raw panel-level mediaKeys from the panel's own config.
 * mediaKeys is a panel-only setting: it never cascades from workspace or section.
 */
export function usePanelMediaKeys(panelRef: PanelTypes.Ref | undefined) {
  return useMemoizedSelector(makePanelMediaKeysSelector, panelRef);
}

/**
 * Whether to show the reset-to-parent button for mediaKeys at the current
 * settings level.
 * mediaKeys is panel-only, so this is only ever true at the panel level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export function useShouldShowMediaKeysResetAtLevel(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): boolean {
  const panelMediaKeys = usePanelMediaKeys(panelRef);
  return settingsLevel === SettingsLevel.PANEL && panelMediaKeys != null;
}

/** Raw workspace-level value from view spec. */
export function useWorkspaceMediaSliderSync() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaSliderSyncSelector,
    workspaceSettingsRef
  );
}

export function useMaybeWorkspaceMediaSliderSync() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaSliderSyncSelector,
    workspaceRefs?.workspaceSettingsRef
  );
}

/** Raw section-level value from view spec (not cascaded from workspace). */
export function useSectionMediaSliderSync() {
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(makeSectionMediaSliderSyncSelector, sectionRef);
}

export function useMaybeSectionMediaSliderSync() {
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(makeSectionMediaSliderSyncSelector, sectionRef);
}

/** Workspace → section cascaded value for display and section-level behavior. */
export function useCascadingSectionMediaSliderSync(): boolean {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaSliderSyncSelector,
    workspaceSettingsRef,
    sectionRef
  );
}

export function useMaybeCascadingSectionMediaSliderSync(): boolean {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaSliderSyncSelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef
  );
}

/**
 * Effective sliderSync for the media settings drawer at workspace or section level.
 * Use this instead of reading `config.sliderSync` for migrated section fields.
 */
export function useMediaSliderSyncDisplay(
  settingsLevel: SettingsLevel
): boolean {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeMediaSliderSyncDisplaySelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    settingsLevel
  );
}

/**
 * Whether to show the reset-to-parent button for sliderSync at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export function useShouldShowMediaSliderSyncResetAtLevel(
  settingsLevel: SettingsLevel
): boolean {
  const sectionSliderSync = useMaybeSectionMediaSliderSync();

  return settingsLevel === SettingsLevel.SECTION && sectionSliderSync != null;
}

/** Raw workspace-level value from view spec. */
export function useWorkspaceMediaSliderKey() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaSliderKeySelector,
    workspaceSettingsRef
  );
}

export function useMaybeWorkspaceMediaSliderKey() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaSliderKeySelector,
    workspaceRefs?.workspaceSettingsRef
  );
}

/** Raw section-level value from view spec (not cascaded from workspace). */
export function useSectionMediaSliderKey() {
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(makeSectionMediaSliderKeySelector, sectionRef);
}

export function useMaybeSectionMediaSliderKey() {
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(makeSectionMediaSliderKeySelector, sectionRef);
}

/** Workspace → section cascaded value for display and section-level behavior. */
export function useCascadingSectionMediaSliderKey(): string {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaSliderKeySelector,
    workspaceSettingsRef,
    sectionRef
  );
}

/** Raw panel-level value from the panel's own config (not cascaded). */
export function usePanelMediaSliderKey(panelRef: PanelTypes.Ref | undefined) {
  return useMemoizedSelector(makePanelMediaSliderKeySelector, panelRef);
}

export function useMaybeCascadingSectionMediaSliderKey(): string {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaSliderKeySelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef
  );
}

/**
 * Effective mediaSliderKey for the media settings drawer at workspace or section level.
 * Use this instead of reading `config.mediaSliderKey` for migrated section fields.
 */
export function useMediaSliderKeyDisplay(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): string {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeMediaSliderKeyDisplaySelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    settingsLevel,
    panelRef
  );
}

/**
 * Whether to show the reset-to-parent button for mediaSliderKey at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export function useShouldShowMediaSliderKeyResetAtLevel(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): boolean {
  const sectionMediaSliderKey = useMaybeSectionMediaSliderKey();
  const panelMediaSliderKey = usePanelMediaSliderKey(panelRef);

  if (settingsLevel === SettingsLevel.PANEL) {
    return panelMediaSliderKey != null;
  }

  return (
    settingsLevel === SettingsLevel.SECTION && sectionMediaSliderKey != null
  );
}

/** Raw workspace-level value from view spec. */
export function useWorkspaceMediaMode() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaModeSelector,
    workspaceSettingsRef
  );
}

export function useMaybeWorkspaceMediaMode() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaModeSelector,
    workspaceRefs?.workspaceSettingsRef
  );
}

/** Raw section-level value from view spec (not cascaded from workspace). */
export function useSectionMediaMode() {
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(makeSectionMediaModeSelector, sectionRef);
}

export function useMaybeSectionMediaMode() {
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(makeSectionMediaModeSelector, sectionRef);
}

/** Workspace → section cascaded value for display and section-level behavior. */
export function useCascadingSectionMediaMode(): LayoutMode {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaModeSelector,
    workspaceSettingsRef,
    sectionRef
  );
}

/** Raw panel-level value from the panel's own config (not cascaded). */
export function usePanelMediaMode(panelRef: PanelTypes.Ref | undefined) {
  return useMemoizedSelector(makePanelMediaModeSelector, panelRef);
}

export function useMaybeCascadingSectionMediaMode(): LayoutMode {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaModeSelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef
  );
}

/**
 * Effective mode for the media settings drawer at the given settings level.
 * Use this instead of reading `config.mode` for migrated fields.
 */
export function useMediaModeDisplay(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): LayoutMode {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeMediaModeDisplaySelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    settingsLevel,
    panelRef
  );
}

/**
 * Whether to show the reset-to-parent button for mode at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export function useShouldShowMediaModeResetAtLevel(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): boolean {
  const sectionMediaMode = useMaybeSectionMediaMode();
  const panelMediaMode = usePanelMediaMode(panelRef);

  if (settingsLevel === SettingsLevel.PANEL) {
    return panelMediaMode != null;
  }

  return settingsLevel === SettingsLevel.SECTION && sectionMediaMode != null;
}

/** Raw workspace-level value from view spec. */
export function useWorkspaceMediaSnapToExistingStep() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaSnapToExistingStepSelector,
    workspaceSettingsRef
  );
}

export function useMaybeWorkspaceMediaSnapToExistingStep() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaSnapToExistingStepSelector,
    workspaceRefs?.workspaceSettingsRef
  );
}

/** Raw section-level value from view spec (not cascaded from workspace). */
export function useSectionMediaSnapToExistingStep() {
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeSectionMediaSnapToExistingStepSelector,
    sectionRef
  );
}

export function useMaybeSectionMediaSnapToExistingStep() {
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeSectionMediaSnapToExistingStepSelector,
    sectionRef
  );
}

/** Workspace → section cascaded value for display and section-level behavior. */
export function useCascadingSectionMediaSnapToExistingStep(): boolean {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaSnapToExistingStepSelector,
    workspaceSettingsRef,
    sectionRef
  );
}

/** Raw panel-level value from the panel's own config (not cascaded). */
export function usePanelMediaSnapToExistingStep(
  panelRef: PanelTypes.Ref | undefined
) {
  return useMemoizedSelector(
    makePanelMediaSnapToExistingStepSelector,
    panelRef
  );
}

export function useMaybeCascadingSectionMediaSnapToExistingStep(): boolean {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaSnapToExistingStepSelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef
  );
}

/**
 * Effective snapToExistingStep for the media settings drawer at the given settings level.
 * Use this instead of reading `config.snapToExistingStep` for migrated fields.
 */
export function useMediaSnapToExistingStepDisplay(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): boolean {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeMediaSnapToExistingStepDisplaySelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    settingsLevel,
    panelRef
  );
}

/**
 * Whether to show the reset-to-parent button for snapToExistingStep at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export function useShouldShowMediaSnapToExistingStepResetAtLevel(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): boolean {
  const sectionSnapToExistingStep = useMaybeSectionMediaSnapToExistingStep();
  const panelSnapToExistingStep = usePanelMediaSnapToExistingStep(panelRef);

  if (settingsLevel === SettingsLevel.PANEL) {
    return panelSnapToExistingStep != null;
  }

  return (
    settingsLevel === SettingsLevel.SECTION && sectionSnapToExistingStep != null
  );
}

/** Raw workspace-level value from view spec. */
export function useWorkspaceMediaStepStrideLength() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaStepStrideLengthSelector,
    workspaceSettingsRef
  );
}

export function useMaybeWorkspaceMediaStepStrideLength() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaStepStrideLengthSelector,
    workspaceRefs?.workspaceSettingsRef
  );
}

/** Raw section-level value from view spec (not cascaded from workspace). */
export function useSectionMediaStepStrideLength() {
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeSectionMediaStepStrideLengthSelector,
    sectionRef
  );
}

export function useMaybeSectionMediaStepStrideLength() {
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeSectionMediaStepStrideLengthSelector,
    sectionRef
  );
}

/** Workspace → section cascaded value for display and section-level behavior. */
export function useCascadingSectionMediaStepStrideLength(): number | undefined {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaStepStrideLengthSelector,
    workspaceSettingsRef,
    sectionRef
  );
}

/** Raw panel-level value from the panel's own config (not cascaded). */
export function usePanelMediaStepStrideLength(
  panelRef: PanelTypes.Ref | undefined
) {
  return useMemoizedSelector(makePanelMediaStepStrideLengthSelector, panelRef);
}

export function useMaybeCascadingSectionMediaStepStrideLength():
  | number
  | undefined {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaStepStrideLengthSelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef
  );
}

/**
 * Effective stepStrideLength for the media settings drawer at the given settings level.
 * Use this instead of reading `config.stepStrideLength` for migrated fields.
 */
export function useMediaStepStrideLengthDisplay(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): number | undefined {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeMediaStepStrideLengthDisplaySelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    settingsLevel,
    panelRef
  );
}

/**
 * Whether to show the reset-to-parent button for stepStrideLength at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export function useShouldShowMediaStepStrideLengthResetAtLevel(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): boolean {
  const sectionStepStrideLength = useMaybeSectionMediaStepStrideLength();
  const panelStepStrideLength = usePanelMediaStepStrideLength(panelRef);

  if (settingsLevel === SettingsLevel.PANEL) {
    return panelStepStrideLength != null;
  }

  return (
    settingsLevel === SettingsLevel.SECTION && sectionStepStrideLength != null
  );
}

/** Raw workspace-level value from view spec. */
export function useWorkspaceMediaPixelated() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaPixelatedSelector,
    workspaceSettingsRef
  );
}

export function useMaybeWorkspaceMediaPixelated() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaPixelatedSelector,
    workspaceRefs?.workspaceSettingsRef
  );
}

/** Raw section-level value from view spec (not cascaded from workspace). */
export function useSectionMediaPixelated() {
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(makeSectionMediaPixelatedSelector, sectionRef);
}

export function useMaybeSectionMediaPixelated() {
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(makeSectionMediaPixelatedSelector, sectionRef);
}

/** Workspace → section cascaded value for display and section-level behavior. */
export function useCascadingSectionMediaPixelated(): boolean {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaPixelatedSelector,
    workspaceSettingsRef,
    sectionRef
  );
}

/** Raw panel-level value from the panel's own config (not cascaded). */
export function usePanelMediaPixelated(panelRef: PanelTypes.Ref | undefined) {
  return useMemoizedSelector(makePanelMediaPixelatedSelector, panelRef);
}

export function useMaybeCascadingSectionMediaPixelated(): boolean {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaPixelatedSelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef
  );
}

/**
 * Effective pixelated for the media settings drawer at the given settings level.
 * Use this instead of reading `config.pixelated` for migrated fields.
 */
export function useMediaPixelatedDisplay(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): boolean {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeMediaPixelatedDisplaySelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    settingsLevel,
    panelRef
  );
}

/**
 * Whether to show the reset-to-parent button for pixelated at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export function useShouldShowMediaPixelatedResetAtLevel(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): boolean {
  const sectionPixelated = useMaybeSectionMediaPixelated();
  const panelPixelated = usePanelMediaPixelated(panelRef);

  if (settingsLevel === SettingsLevel.PANEL) {
    return panelPixelated != null;
  }

  return settingsLevel === SettingsLevel.SECTION && sectionPixelated != null;
}

/** Raw workspace-level value from view spec. */
export function useWorkspaceMediaColumnCount() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaColumnCountSelector,
    workspaceSettingsRef
  );
}

export function useMaybeWorkspaceMediaColumnCount() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaColumnCountSelector,
    workspaceRefs?.workspaceSettingsRef
  );
}

/** Raw section-level value from view spec (not cascaded from workspace). */
export function useSectionMediaColumnCount() {
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(makeSectionMediaColumnCountSelector, sectionRef);
}

export function useMaybeSectionMediaColumnCount() {
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(makeSectionMediaColumnCountSelector, sectionRef);
}

/** Workspace → section cascaded value for display and section-level behavior. */
export function useCascadingSectionMediaColumnCount(): number | undefined {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaColumnCountSelector,
    workspaceSettingsRef,
    sectionRef
  );
}

/** Raw panel-level value from the panel's own config (not cascaded). */
export function usePanelMediaColumnCount(panelRef: PanelTypes.Ref | undefined) {
  return useMemoizedSelector(makePanelMediaColumnCountSelector, panelRef);
}

export function useMaybeCascadingSectionMediaColumnCount(): number | undefined {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaColumnCountSelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef
  );
}

/**
 * Effective columnCount for the media settings drawer at the given settings level.
 * Use this instead of reading `config.columnCount` for migrated fields.
 */
export function useMediaColumnCountDisplay(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): number {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeMediaColumnCountDisplaySelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    settingsLevel,
    panelRef
  );
}

/**
 * Whether to show the reset-to-parent button for columnCount at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export function useShouldShowMediaColumnCountResetAtLevel(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): boolean {
  const sectionColumnCount = useMaybeSectionMediaColumnCount();
  const panelColumnCount = usePanelMediaColumnCount(panelRef);

  if (settingsLevel === SettingsLevel.PANEL) {
    return panelColumnCount != null;
  }

  return settingsLevel === SettingsLevel.SECTION && sectionColumnCount != null;
}

/** Raw workspace-level value from view spec. */
export function useWorkspaceMediaMaxRuns() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaMaxRunsSelector,
    workspaceSettingsRef
  );
}

export function useMaybeWorkspaceMediaMaxRuns() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaMaxRunsSelector,
    workspaceRefs?.workspaceSettingsRef
  );
}

/** Raw section-level value from view spec (not cascaded from workspace). */
export function useSectionMediaMaxRuns() {
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(makeSectionMediaMaxRunsSelector, sectionRef);
}

export function useMaybeSectionMediaMaxRuns() {
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(makeSectionMediaMaxRunsSelector, sectionRef);
}

/** Workspace → section cascaded value for display and section-level behavior. */
export function useCascadingSectionMediaMaxRuns(): number {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaMaxRunsSelector,
    workspaceSettingsRef,
    sectionRef
  );
}

/** Raw panel-level value from the panel's own config (not cascaded). */
export function usePanelMediaMaxRuns(panelRef: PanelTypes.Ref | undefined) {
  return useMemoizedSelector(makePanelMediaMaxRunsSelector, panelRef);
}

export function useMaybeCascadingSectionMediaMaxRuns(): number {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaMaxRunsSelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef
  );
}

/**
 * Effective maxRuns for the media settings drawer at the given settings level.
 * Use this instead of reading `config.maxRuns` for migrated fields.
 */
export function useMediaMaxRunsDisplay(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): number {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeMediaMaxRunsDisplaySelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    settingsLevel,
    panelRef
  );
}

/**
 * Whether to show the reset-to-parent button for maxRuns at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export function useShouldShowMediaMaxRunsResetAtLevel(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): boolean {
  const sectionMaxRuns = useMaybeSectionMediaMaxRuns();
  const panelMaxRuns = usePanelMediaMaxRuns(panelRef);

  if (settingsLevel === SettingsLevel.PANEL) {
    return panelMaxRuns != null;
  }

  return settingsLevel === SettingsLevel.SECTION && sectionMaxRuns != null;
}

/** Raw workspace-level value from view spec. */
export function useWorkspaceMediaMaxGalleryItems() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaMaxGalleryItemsSelector,
    workspaceSettingsRef
  );
}

export function useMaybeWorkspaceMediaMaxGalleryItems() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaMaxGalleryItemsSelector,
    workspaceRefs?.workspaceSettingsRef
  );
}

/** Raw section-level value from view spec (not cascaded from workspace). */
export function useSectionMediaMaxGalleryItems() {
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeSectionMediaMaxGalleryItemsSelector,
    sectionRef
  );
}

export function useMaybeSectionMediaMaxGalleryItems() {
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeSectionMediaMaxGalleryItemsSelector,
    sectionRef
  );
}

/** Workspace → section cascaded value for display and section-level behavior. */
export function useCascadingSectionMediaMaxGalleryItems(): number | undefined {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaMaxGalleryItemsSelector,
    workspaceSettingsRef,
    sectionRef
  );
}

/** Raw panel-level value from the panel's own config (not cascaded). */
export function usePanelMediaMaxGalleryItems(
  panelRef: PanelTypes.Ref | undefined
) {
  return useMemoizedSelector(makePanelMediaMaxGalleryItemsSelector, panelRef);
}

export function useMaybeCascadingSectionMediaMaxGalleryItems():
  | number
  | undefined {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaMaxGalleryItemsSelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef
  );
}

/**
 * Effective maxGalleryItems for the media settings drawer at the given settings level.
 * Use this instead of reading `config.maxGalleryItems` for migrated fields.
 */
export function useMediaMaxGalleryItemsDisplay(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): number | undefined {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeMediaMaxGalleryItemsDisplaySelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    settingsLevel,
    panelRef
  );
}

/**
 * Whether to show the reset-to-parent button for maxGalleryItems at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export function useShouldShowMediaMaxGalleryItemsResetAtLevel(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): boolean {
  const sectionMaxGalleryItems = useMaybeSectionMediaMaxGalleryItems();
  const panelMaxGalleryItems = usePanelMediaMaxGalleryItems(panelRef);

  if (settingsLevel === SettingsLevel.PANEL) {
    return panelMaxGalleryItems != null;
  }

  return (
    settingsLevel === SettingsLevel.SECTION && sectionMaxGalleryItems != null
  );
}

/** Raw workspace-level value from view spec. */
export function useWorkspaceMediaMaxYAxisCount() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaMaxYAxisCountSelector,
    workspaceSettingsRef
  );
}

export function useMaybeWorkspaceMediaMaxYAxisCount() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaMaxYAxisCountSelector,
    workspaceRefs?.workspaceSettingsRef
  );
}

/** Raw section-level value from view spec (not cascaded from workspace). */
export function useSectionMediaMaxYAxisCount() {
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(makeSectionMediaMaxYAxisCountSelector, sectionRef);
}

export function useMaybeSectionMediaMaxYAxisCount() {
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(makeSectionMediaMaxYAxisCountSelector, sectionRef);
}

/** Workspace → section cascaded value for display and section-level behavior. */
export function useCascadingSectionMediaMaxYAxisCount(): number {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaMaxYAxisCountSelector,
    workspaceSettingsRef,
    sectionRef
  );
}

/** Raw panel-level value from the panel's own config (not cascaded). */
export function usePanelMediaMaxYAxisCount(
  panelRef: PanelTypes.Ref | undefined
) {
  return useMemoizedSelector(makePanelMediaMaxYAxisCountSelector, panelRef);
}

export function useMaybeCascadingSectionMediaMaxYAxisCount(): number {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaMaxYAxisCountSelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef
  );
}

/**
 * Effective maxYAxisCount for the media settings drawer at the given settings level.
 * Use this instead of reading `config.maxYAxisCount` for migrated fields.
 */
export function useMediaMaxYAxisCountDisplay(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): number {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeMediaMaxYAxisCountDisplaySelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    settingsLevel,
    panelRef
  );
}

/**
 * Whether to show the reset-to-parent button for maxYAxisCount at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export function useShouldShowMediaMaxYAxisCountResetAtLevel(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): boolean {
  const sectionMaxYAxisCount = useMaybeSectionMediaMaxYAxisCount();
  const panelMaxYAxisCount = usePanelMediaMaxYAxisCount(panelRef);

  if (settingsLevel === SettingsLevel.PANEL) {
    return panelMaxYAxisCount != null;
  }

  return (
    settingsLevel === SettingsLevel.SECTION && sectionMaxYAxisCount != null
  );
}

/** Raw workspace-level value from view spec. */
export function useWorkspaceMediaActualSize() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaActualSizeSelector,
    workspaceSettingsRef
  );
}

export function useMaybeWorkspaceMediaActualSize() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaActualSizeSelector,
    workspaceRefs?.workspaceSettingsRef
  );
}

/** Raw section-level value from view spec (not cascaded from workspace). */
export function useSectionMediaActualSize() {
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(makeSectionMediaActualSizeSelector, sectionRef);
}

export function useMaybeSectionMediaActualSize() {
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(makeSectionMediaActualSizeSelector, sectionRef);
}

/** Workspace → section cascaded value for display and section-level behavior. */
export function useCascadingSectionMediaActualSize(): boolean {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaActualSizeSelector,
    workspaceSettingsRef,
    sectionRef
  );
}

/** Raw panel-level value from the panel's own config (not cascaded). */
export function usePanelMediaActualSize(panelRef: PanelTypes.Ref | undefined) {
  return useMemoizedSelector(makePanelMediaActualSizeSelector, panelRef);
}

export function useMaybeCascadingSectionMediaActualSize(): boolean {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaActualSizeSelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef
  );
}

/**
 * Effective actualSize for the media settings drawer at the given settings level.
 * Use this instead of reading `config.actualSize` for migrated fields.
 */
export function useMediaActualSizeDisplay(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): boolean {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeMediaActualSizeDisplaySelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    settingsLevel,
    panelRef
  );
}

/**
 * Whether to show the reset-to-parent button for actualSize at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export function useShouldShowMediaActualSizeResetAtLevel(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): boolean {
  const sectionActualSize = useMaybeSectionMediaActualSize();
  const panelActualSize = usePanelMediaActualSize(panelRef);

  if (settingsLevel === SettingsLevel.PANEL) {
    return panelActualSize != null;
  }

  return settingsLevel === SettingsLevel.SECTION && sectionActualSize != null;
}

/** Raw workspace-level value from view spec. */
export function useWorkspaceMediaFitToDimension() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaFitToDimensionSelector,
    workspaceSettingsRef
  );
}

export function useMaybeWorkspaceMediaFitToDimension() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaFitToDimensionSelector,
    workspaceRefs?.workspaceSettingsRef
  );
}

/** Raw section-level value from view spec (not cascaded from workspace). */
export function useSectionMediaFitToDimension() {
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeSectionMediaFitToDimensionSelector,
    sectionRef
  );
}

export function useMaybeSectionMediaFitToDimension() {
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeSectionMediaFitToDimensionSelector,
    sectionRef
  );
}

/** Workspace → section cascaded value for display and section-level behavior. */
export function useCascadingSectionMediaFitToDimension(): boolean {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaFitToDimensionSelector,
    workspaceSettingsRef,
    sectionRef
  );
}

/** Raw panel-level value from the panel's own config (not cascaded). */
export function usePanelMediaFitToDimension(
  panelRef: PanelTypes.Ref | undefined
) {
  return useMemoizedSelector(makePanelMediaFitToDimensionSelector, panelRef);
}

export function useMaybeCascadingSectionMediaFitToDimension(): boolean {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaFitToDimensionSelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef
  );
}

/**
 * Effective fitToDimension for the media settings drawer at the given settings level.
 * Use this instead of reading `config.fitToDimension` for migrated fields.
 */
export function useMediaFitToDimensionDisplay(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): boolean {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeMediaFitToDimensionDisplaySelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    settingsLevel,
    panelRef
  );
}

/**
 * Whether to show the reset-to-parent button for fitToDimension at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export function useShouldShowMediaFitToDimensionResetAtLevel(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): boolean {
  const sectionFitToDimension = useMaybeSectionMediaFitToDimension();
  const panelFitToDimension = usePanelMediaFitToDimension(panelRef);

  if (settingsLevel === SettingsLevel.PANEL) {
    return panelFitToDimension != null;
  }

  return (
    settingsLevel === SettingsLevel.SECTION && sectionFitToDimension != null
  );
}

/** Raw workspace-level value from view spec. */
export function useWorkspaceMediaGallerySettings() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaGallerySettingsSelector,
    workspaceSettingsRef
  );
}

export function useMaybeWorkspaceMediaGallerySettings() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaGallerySettingsSelector,
    workspaceRefs?.workspaceSettingsRef
  );
}

/** Raw section-level value from view spec (not cascaded from workspace). */
export function useSectionMediaGallerySettings() {
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeSectionMediaGallerySettingsSelector,
    sectionRef
  );
}

export function useMaybeSectionMediaGallerySettings() {
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeSectionMediaGallerySettingsSelector,
    sectionRef
  );
}

/** Workspace → section cascaded value for display and section-level behavior. */
export function useCascadingSectionMediaGallerySettings() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContext().sectionRef;
  const {isSingleRun} = useSingleRunModeContext();
  return useMemoizedSelector(
    makeCascadingSectionMediaGallerySettingsSelector,
    workspaceSettingsRef,
    sectionRef,
    isSingleRun
  );
}

/** Raw panel-level value from the panel's own config (not cascaded). */
export function usePanelMediaGallerySettings(
  panelRef: PanelTypes.Ref | undefined
) {
  return useMemoizedSelector(makePanelMediaGallerySettingsSelector, panelRef);
}

export function useMaybeCascadingSectionMediaGallerySettings() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  const {isSingleRun} = useSingleRunModeContext();
  return useMemoizedSelector(
    makeCascadingSectionMediaGallerySettingsSelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    isSingleRun
  );
}

/**
 * Effective gallerySettings for the media settings drawer at the given settings level.
 * Use this instead of reading `config.gallerySettings` for migrated fields.
 */
export function useMediaGallerySettingsDisplay(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
) {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  const {isSingleRun} = useSingleRunModeContext();
  return useMemoizedSelector(
    makeMediaGallerySettingsDisplaySelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    settingsLevel,
    panelRef,
    isSingleRun
  );
}

/**
 * Whether to show the reset-to-parent button for gallerySettings at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export function useShouldShowMediaGallerySettingsResetAtLevel(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): boolean {
  const sectionGallerySettings = useMaybeSectionMediaGallerySettings();
  const panelGallerySettings = usePanelMediaGallerySettings(panelRef);

  if (settingsLevel === SettingsLevel.PANEL) {
    return panelGallerySettings != null;
  }

  return (
    settingsLevel === SettingsLevel.SECTION && sectionGallerySettings != null
  );
}

/** Raw workspace-level value from view spec. */
export function useWorkspaceMediaGridSettings() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaGridSettingsSelector,
    workspaceSettingsRef
  );
}

export function useMaybeWorkspaceMediaGridSettings() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaGridSettingsSelector,
    workspaceRefs?.workspaceSettingsRef
  );
}

/** Raw section-level value from view spec (not cascaded from workspace). */
export function useSectionMediaGridSettings() {
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(makeSectionMediaGridSettingsSelector, sectionRef);
}

export function useMaybeSectionMediaGridSettings() {
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(makeSectionMediaGridSettingsSelector, sectionRef);
}

/** Workspace → section cascaded value for display and section-level behavior. */
export function useCascadingSectionMediaGridSettings() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContext().sectionRef;
  const {isSingleRun} = useSingleRunModeContext();
  return useMemoizedSelector(
    makeCascadingSectionMediaGridSettingsSelector,
    workspaceSettingsRef,
    sectionRef,
    isSingleRun
  );
}

/** Raw panel-level value from the panel's own config (not cascaded). */
export function usePanelMediaGridSettings(
  panelRef: PanelTypes.Ref | undefined
) {
  return useMemoizedSelector(makePanelMediaGridSettingsSelector, panelRef);
}

export function useMaybeCascadingSectionMediaGridSettings() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  const {isSingleRun} = useSingleRunModeContext();
  return useMemoizedSelector(
    makeCascadingSectionMediaGridSettingsSelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    isSingleRun
  );
}

/**
 * Effective gridSettings for the media settings drawer at the given settings level.
 * Use this instead of reading `config.gridSettings` for migrated fields.
 */
export function useMediaGridSettingsDisplay(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
) {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  const {isSingleRun} = useSingleRunModeContext();
  return useMemoizedSelector(
    makeMediaGridSettingsDisplaySelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    settingsLevel,
    panelRef,
    isSingleRun
  );
}

/**
 * Whether to show the reset-to-parent button for gridSettings at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export function useShouldShowMediaGridSettingsResetAtLevel(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): boolean {
  const sectionGridSettings = useMaybeSectionMediaGridSettings();
  const panelGridSettings = usePanelMediaGridSettings(panelRef);

  if (settingsLevel === SettingsLevel.PANEL) {
    return panelGridSettings != null;
  }

  return settingsLevel === SettingsLevel.SECTION && sectionGridSettings != null;
}

/** Raw workspace-level value from view spec. */
export function useWorkspaceMediaVideoPlaybackSync() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaVideoPlaybackSyncSelector,
    workspaceSettingsRef
  );
}

export function useMaybeWorkspaceMediaVideoPlaybackSync() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaVideoPlaybackSyncSelector,
    workspaceRefs?.workspaceSettingsRef
  );
}

/** Raw section-level value from view spec (not cascaded from workspace). */
export function useSectionMediaVideoPlaybackSync() {
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeSectionMediaVideoPlaybackSyncSelector,
    sectionRef
  );
}

export function useMaybeSectionMediaVideoPlaybackSync() {
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeSectionMediaVideoPlaybackSyncSelector,
    sectionRef
  );
}

/** Workspace → section cascaded value; the higher level takes precedence. */
export function useCascadingSectionMediaVideoPlaybackSync(): boolean {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaVideoPlaybackSyncSelector,
    workspaceSettingsRef,
    sectionRef
  );
}

/** Raw panel-level value from the panel's own config (not cascaded). */
export function usePanelMediaVideoPlaybackSync(
  panelRef: PanelTypes.Ref | undefined
) {
  return useMemoizedSelector(makePanelMediaVideoPlaybackSyncSelector, panelRef);
}

export function useMaybeCascadingSectionMediaVideoPlaybackSync(): boolean {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaVideoPlaybackSyncSelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef
  );
}

/**
 * Effective videoPlaybackSync for the media settings drawer at the given settings level.
 * Use this instead of reading `config.videoPlaybackSync` for migrated fields.
 */
export function useMediaVideoPlaybackSyncDisplay(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): boolean {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeMediaVideoPlaybackSyncDisplaySelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    settingsLevel,
    panelRef
  );
}

/**
 * Whether the sync toggle is locked because a higher level enabled it.
 */
export function useMediaVideoPlaybackSyncIsInherited(
  settingsLevel: SettingsLevel
): boolean {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeMediaVideoPlaybackSyncIsInheritedSelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    settingsLevel
  );
}

/**
 * Whether to show the reset-to-parent button for videoPlaybackSync at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export function useShouldShowMediaVideoPlaybackSyncResetAtLevel(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): boolean {
  const sectionVideoPlaybackSync = useMaybeSectionMediaVideoPlaybackSync();
  const panelVideoPlaybackSync = usePanelMediaVideoPlaybackSync(panelRef);

  if (settingsLevel === SettingsLevel.PANEL) {
    return panelVideoPlaybackSync != null;
  }

  return (
    settingsLevel === SettingsLevel.SECTION && sectionVideoPlaybackSync != null
  );
}

/** Raw workspace-level value from view spec. */
export function useWorkspaceMediaVideoPlaybackSyncAutoplay() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceSettingsVideoPlaybackSyncAutoplaySelector,
    workspaceSettingsRef
  );
}

export function useMaybeWorkspaceMediaVideoPlaybackSyncAutoplay() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceSettingsVideoPlaybackSyncAutoplaySelector,
    workspaceRefs?.workspaceSettingsRef
  );
}

/** Raw section-level value from view spec (not cascaded from workspace). */
export function useSectionMediaVideoPlaybackSyncAutoplay() {
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeSectionMediaVideoPlaybackSyncAutoplaySelector,
    sectionRef
  );
}

export function useMaybeSectionMediaVideoPlaybackSyncAutoplay() {
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeSectionMediaVideoPlaybackSyncAutoplaySelector,
    sectionRef
  );
}

/** Workspace → section cascaded value with default. */
export function useCascadingSectionMediaVideoPlaybackSyncAutoplay(): boolean {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaVideoPlaybackSyncAutoplaySelector,
    workspaceSettingsRef,
    sectionRef
  );
}

/** Raw panel-level value from the panel's own config (not cascaded). */
export function usePanelMediaVideoPlaybackSyncAutoplay(
  panelRef: PanelTypes.Ref | undefined
) {
  return useMemoizedSelector(
    makePanelMediaVideoPlaybackSyncAutoplaySelector,
    panelRef
  );
}

export function useMaybeCascadingSectionMediaVideoPlaybackSyncAutoplay(): boolean {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaVideoPlaybackSyncAutoplaySelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef
  );
}

/**
 * Effective videoPlaybackSyncAutoplay for the media settings drawer at the given settings level.
 * Use this instead of reading `config.videoPlaybackSyncAutoplay` for migrated fields.
 */
export function useMediaVideoPlaybackSyncAutoplayDisplay(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): boolean {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeMediaVideoPlaybackSyncAutoplayDisplaySelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    settingsLevel,
    panelRef
  );
}

/**
 * Whether to show the reset-to-parent button for videoPlaybackSyncAutoplay at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export function useShouldShowMediaVideoPlaybackSyncAutoplayResetAtLevel(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): boolean {
  const sectionVideoPlaybackSyncAutoplay =
    useMaybeSectionMediaVideoPlaybackSyncAutoplay();
  const panelVideoPlaybackSyncAutoplay =
    usePanelMediaVideoPlaybackSyncAutoplay(panelRef);

  if (settingsLevel === SettingsLevel.PANEL) {
    return panelVideoPlaybackSyncAutoplay != null;
  }

  return (
    settingsLevel === SettingsLevel.SECTION &&
    sectionVideoPlaybackSyncAutoplay != null
  );
}

/** Raw workspace-level value from view spec. */
export function useWorkspaceMediaVideoPlaybackSyncLoop() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceSettingsVideoPlaybackSyncLoopSelector,
    workspaceSettingsRef
  );
}

export function useMaybeWorkspaceMediaVideoPlaybackSyncLoop() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceSettingsVideoPlaybackSyncLoopSelector,
    workspaceRefs?.workspaceSettingsRef
  );
}

/** Raw section-level value from view spec (not cascaded from workspace). */
export function useSectionMediaVideoPlaybackSyncLoop() {
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeSectionMediaVideoPlaybackSyncLoopSelector,
    sectionRef
  );
}

export function useMaybeSectionMediaVideoPlaybackSyncLoop() {
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeSectionMediaVideoPlaybackSyncLoopSelector,
    sectionRef
  );
}

/** Workspace → section cascaded value with default. */
export function useCascadingSectionMediaVideoPlaybackSyncLoop(): boolean {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaVideoPlaybackSyncLoopSelector,
    workspaceSettingsRef,
    sectionRef
  );
}

/** Raw panel-level value from the panel's own config (not cascaded). */
export function usePanelMediaVideoPlaybackSyncLoop(
  panelRef: PanelTypes.Ref | undefined
) {
  return useMemoizedSelector(
    makePanelMediaVideoPlaybackSyncLoopSelector,
    panelRef
  );
}

export function useMaybeCascadingSectionMediaVideoPlaybackSyncLoop(): boolean {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaVideoPlaybackSyncLoopSelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef
  );
}

/**
 * Effective videoPlaybackSyncLoop for the media settings drawer at the given settings level.
 * Use this instead of reading `config.videoPlaybackSyncLoop` for migrated fields.
 */
export function useMediaVideoPlaybackSyncLoopDisplay(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): boolean {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeMediaVideoPlaybackSyncLoopDisplaySelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    settingsLevel,
    panelRef
  );
}

/**
 * Whether to show the reset-to-parent button for videoPlaybackSyncLoop at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export function useShouldShowMediaVideoPlaybackSyncLoopResetAtLevel(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): boolean {
  const sectionVideoPlaybackSyncLoop =
    useMaybeSectionMediaVideoPlaybackSyncLoop();
  const panelVideoPlaybackSyncLoop =
    usePanelMediaVideoPlaybackSyncLoop(panelRef);

  if (settingsLevel === SettingsLevel.PANEL) {
    return panelVideoPlaybackSyncLoop != null;
  }

  return (
    settingsLevel === SettingsLevel.SECTION &&
    sectionVideoPlaybackSyncLoop != null
  );
}

/** Raw workspace-level value from view spec. */
export function useWorkspaceMediaVideoPlayerTimeStep() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaVideoPlayerTimeStepSelector,
    workspaceSettingsRef
  );
}

export function useMaybeWorkspaceMediaVideoPlayerTimeStep() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaVideoPlayerTimeStepSelector,
    workspaceRefs?.workspaceSettingsRef
  );
}

/** Raw section-level value from view spec (not cascaded from workspace). */
export function useSectionMediaVideoPlayerTimeStep() {
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeSectionMediaVideoPlayerTimeStepSelector,
    sectionRef
  );
}

export function useMaybeSectionMediaVideoPlayerTimeStep() {
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeSectionMediaVideoPlayerTimeStepSelector,
    sectionRef
  );
}

/** Workspace → section cascaded value. */
export function useCascadingSectionMediaVideoPlayerTimeStep() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaVideoPlayerTimeStepSelector,
    workspaceSettingsRef,
    sectionRef
  );
}

/** Raw panel-level value from the panel's own config (not cascaded). */
export function usePanelMediaVideoPlayerTimeStep(
  panelRef: PanelTypes.Ref | undefined
) {
  return useMemoizedSelector(
    makePanelMediaVideoPlayerTimeStepSelector,
    panelRef
  );
}

export function useMaybeCascadingSectionMediaVideoPlayerTimeStep() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaVideoPlayerTimeStepSelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef
  );
}

/**
 * Effective videoPlayerTimeStep for the media settings drawer at the given settings level.
 * Use this instead of reading `config.videoPlayerTimeStep` for migrated fields.
 */
export function useMediaVideoPlayerTimeStepDisplay(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
) {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeMediaVideoPlayerTimeStepDisplaySelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    settingsLevel,
    panelRef
  );
}

/**
 * Whether to show the reset-to-parent button for videoPlayerTimeStep at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export function useShouldShowMediaVideoPlayerTimeStepResetAtLevel(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): boolean {
  const sectionVideoPlayerTimeStep = useMaybeSectionMediaVideoPlayerTimeStep();
  const panelVideoPlayerTimeStep = usePanelMediaVideoPlayerTimeStep(panelRef);

  if (settingsLevel === SettingsLevel.PANEL) {
    return panelVideoPlayerTimeStep != null;
  }

  return (
    settingsLevel === SettingsLevel.SECTION &&
    sectionVideoPlayerTimeStep != null
  );
}

/** Raw workspace-level value from view spec. */
export function useWorkspaceMediaCompareSettings() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaCompareSettingsSelector,
    workspaceSettingsRef
  );
}

export function useMaybeWorkspaceMediaCompareSettings() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaCompareSettingsSelector,
    workspaceRefs?.workspaceSettingsRef
  );
}

/** Raw section-level value from view spec (not cascaded from workspace). */
export function useSectionMediaCompareSettings() {
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeSectionMediaCompareSettingsSelector,
    sectionRef
  );
}

export function useMaybeSectionMediaCompareSettings() {
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeSectionMediaCompareSettingsSelector,
    sectionRef
  );
}

/** Workspace → section cascaded value (no default; undefined means default compare behavior). */
export function useCascadingSectionMediaCompareSettings() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaCompareSettingsSelector,
    workspaceSettingsRef,
    sectionRef
  );
}

/** Raw panel-level value from the panel's own config (not cascaded). */
export function usePanelMediaCompareSettings(
  panelRef: PanelTypes.Ref | undefined
) {
  return useMemoizedSelector(makePanelMediaCompareSettingsSelector, panelRef);
}

export function useMaybeCascadingSectionMediaCompareSettings() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaCompareSettingsSelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef
  );
}

/**
 * Effective compareSettings for the media settings drawer at the given settings level.
 * Use this instead of reading `config.compareSettings` for migrated fields.
 */
export function useMediaCompareSettingsDisplay(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
) {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeMediaCompareSettingsDisplaySelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    settingsLevel,
    panelRef
  );
}

/**
 * Whether to show the reset-to-parent button for compareSettings at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export function useShouldShowMediaCompareSettingsResetAtLevel(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): boolean {
  const sectionCompareSettings = useMaybeSectionMediaCompareSettings();
  const panelCompareSettings = usePanelMediaCompareSettings(panelRef);

  if (settingsLevel === SettingsLevel.PANEL) {
    return panelCompareSettings != null;
  }

  return (
    settingsLevel === SettingsLevel.SECTION && sectionCompareSettings != null
  );
}

/** Raw workspace-level value from view spec. */
export function useWorkspaceMediaBoundingBoxConfig() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaBoundingBoxConfigSelector,
    workspaceSettingsRef
  );
}

export function useMaybeWorkspaceMediaBoundingBoxConfig() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaBoundingBoxConfigSelector,
    workspaceRefs?.workspaceSettingsRef
  );
}

/** Raw section-level value from view spec (not cascaded from workspace). */
export function useSectionMediaBoundingBoxConfig() {
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeSectionMediaBoundingBoxConfigSelector,
    sectionRef
  );
}

export function useMaybeSectionMediaBoundingBoxConfig() {
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeSectionMediaBoundingBoxConfigSelector,
    sectionRef
  );
}

/** Workspace → section cascaded value (no default; undefined means default bounding box behavior). */
export function useCascadingSectionMediaBoundingBoxConfig() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaBoundingBoxConfigSelector,
    workspaceSettingsRef,
    sectionRef
  );
}

/** Raw panel-level value from the panel's own config (not cascaded). */
export function usePanelMediaBoundingBoxConfig(
  panelRef: PanelTypes.Ref | undefined
) {
  return useMemoizedSelector(makePanelMediaBoundingBoxConfigSelector, panelRef);
}

export function useMaybeCascadingSectionMediaBoundingBoxConfig() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaBoundingBoxConfigSelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef
  );
}

/**
 * Effective boundingBoxConfig for the media settings drawer at the given settings level.
 * Use this instead of reading `config.boundingBoxConfig` for migrated fields.
 */
export function useMediaBoundingBoxConfigDisplay(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
) {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeMediaBoundingBoxConfigDisplaySelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    settingsLevel,
    panelRef
  );
}

/**
 * Whether to show the reset-to-parent button for boundingBoxConfig at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export function useShouldShowMediaBoundingBoxConfigResetAtLevel(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): boolean {
  const sectionBoundingBoxConfig = useMaybeSectionMediaBoundingBoxConfig();
  const panelBoundingBoxConfig = usePanelMediaBoundingBoxConfig(panelRef);

  if (settingsLevel === SettingsLevel.PANEL) {
    return panelBoundingBoxConfig != null;
  }

  return (
    settingsLevel === SettingsLevel.SECTION && sectionBoundingBoxConfig != null
  );
}

/** Raw workspace-level value from view spec. */
export function useWorkspaceMediaSegmentationMaskConfig() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaSegmentationMaskConfigSelector,
    workspaceSettingsRef
  );
}

export function useMaybeWorkspaceMediaSegmentationMaskConfig() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaSegmentationMaskConfigSelector,
    workspaceRefs?.workspaceSettingsRef
  );
}

/** Raw section-level value from view spec (not cascaded from workspace). */
export function useSectionMediaSegmentationMaskConfig() {
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeSectionMediaSegmentationMaskConfigSelector,
    sectionRef
  );
}

export function useMaybeSectionMediaSegmentationMaskConfig() {
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeSectionMediaSegmentationMaskConfigSelector,
    sectionRef
  );
}

/** Workspace → section cascaded value (no default; undefined means default segmentation mask behavior). */
export function useCascadingSectionMediaSegmentationMaskConfig() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaSegmentationMaskConfigSelector,
    workspaceSettingsRef,
    sectionRef
  );
}

/** Raw panel-level value from the panel's own config (not cascaded). */
export function usePanelMediaSegmentationMaskConfig(
  panelRef: PanelTypes.Ref | undefined
) {
  return useMemoizedSelector(
    makePanelMediaSegmentationMaskConfigSelector,
    panelRef
  );
}

export function useMaybeCascadingSectionMediaSegmentationMaskConfig() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaSegmentationMaskConfigSelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef
  );
}

/**
 * Effective segmentationMaskConfig for the media settings drawer at the given settings level.
 * Use this instead of reading `config.segmentationMaskConfig` for migrated fields.
 */
export function useMediaSegmentationMaskConfigDisplay(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
) {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeMediaSegmentationMaskConfigDisplaySelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    settingsLevel,
    panelRef
  );
}

/**
 * Whether to show the reset-to-parent button for segmentationMaskConfig at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export function useShouldShowMediaSegmentationMaskConfigResetAtLevel(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): boolean {
  const sectionSegmentationMaskConfig =
    useMaybeSectionMediaSegmentationMaskConfig();
  const panelSegmentationMaskConfig =
    usePanelMediaSegmentationMaskConfig(panelRef);

  if (settingsLevel === SettingsLevel.PANEL) {
    return panelSegmentationMaskConfig != null;
  }

  return (
    settingsLevel === SettingsLevel.SECTION &&
    sectionSegmentationMaskConfig != null
  );
}

/** Raw workspace-level value from view spec. */
export function useWorkspaceMediaUseRightHandedSystem() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaUseRightHandedSystemSelector,
    workspaceSettingsRef
  );
}

export function useMaybeWorkspaceMediaUseRightHandedSystem() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaUseRightHandedSystemSelector,
    workspaceRefs?.workspaceSettingsRef
  );
}

/** Raw section-level value from view spec (not cascaded from workspace). */
export function useSectionMediaUseRightHandedSystem() {
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeSectionMediaUseRightHandedSystemSelector,
    sectionRef
  );
}

export function useMaybeSectionMediaUseRightHandedSystem() {
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeSectionMediaUseRightHandedSystemSelector,
    sectionRef
  );
}

/** Workspace → section cascaded value with default (false). */
export function useCascadingSectionMediaUseRightHandedSystem() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaUseRightHandedSystemSelector,
    workspaceSettingsRef,
    sectionRef
  );
}

/** Raw panel-level value from the panel's own config (not cascaded). */
export function usePanelMediaUseRightHandedSystem(
  panelRef: PanelTypes.Ref | undefined
) {
  return useMemoizedSelector(
    makePanelMediaUseRightHandedSystemSelector,
    panelRef
  );
}

export function useMaybeCascadingSectionMediaUseRightHandedSystem() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaUseRightHandedSystemSelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef
  );
}

/**
 * Effective useRightHandedSystem for the media settings drawer at the given settings level.
 * Use this instead of reading `config.useRightHandedSystem` for migrated fields.
 */
export function useMediaUseRightHandedSystemDisplay(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): boolean {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeMediaUseRightHandedSystemDisplaySelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    settingsLevel,
    panelRef
  );
}

/**
 * Whether to show the reset-to-parent button for useRightHandedSystem at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export function useShouldShowMediaUseRightHandedSystemResetAtLevel(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): boolean {
  const sectionUseRightHandedSystem =
    useMaybeSectionMediaUseRightHandedSystem();
  const panelUseRightHandedSystem = usePanelMediaUseRightHandedSystem(panelRef);

  if (settingsLevel === SettingsLevel.PANEL) {
    return panelUseRightHandedSystem != null;
  }

  return (
    settingsLevel === SettingsLevel.SECTION &&
    sectionUseRightHandedSystem != null
  );
}

/** Raw workspace-level value from view spec. */
export function useWorkspaceMediaPointColor() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaPointColorSelector,
    workspaceSettingsRef
  );
}

export function useMaybeWorkspaceMediaPointColor() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaPointColorSelector,
    workspaceRefs?.workspaceSettingsRef
  );
}

/** Raw section-level value from view spec (not cascaded from workspace). */
export function useSectionMediaPointColor() {
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(makeSectionMediaPointColorSelector, sectionRef);
}

export function useMaybeSectionMediaPointColor() {
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(makeSectionMediaPointColorSelector, sectionRef);
}

/** Workspace → section cascaded value (no default; undefined means default logged color). */
export function useCascadingSectionMediaPointColor() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaPointColorSelector,
    workspaceSettingsRef,
    sectionRef
  );
}

/** Raw panel-level value from the panel's own config (not cascaded). */
export function usePanelMediaPointColor(panelRef: PanelTypes.Ref | undefined) {
  return useMemoizedSelector(makePanelMediaPointColorSelector, panelRef);
}

export function useMaybeCascadingSectionMediaPointColor() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaPointColorSelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef
  );
}

/**
 * Effective pointColor for the media settings drawer at the given settings level.
 * Use this instead of reading `config.pointColor` for migrated fields.
 */
export function useMediaPointColorDisplay(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
) {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeMediaPointColorDisplaySelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    settingsLevel,
    panelRef
  );
}

/**
 * Whether to show the reset-to-parent button for pointColor at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export function useShouldShowMediaPointColorResetAtLevel(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): boolean {
  const sectionPointColor = useMaybeSectionMediaPointColor();
  const panelPointColor = usePanelMediaPointColor(panelRef);

  if (settingsLevel === SettingsLevel.PANEL) {
    return panelPointColor != null;
  }

  return settingsLevel === SettingsLevel.SECTION && sectionPointColor != null;
}

/** Raw workspace-level value from view spec. */
export function useWorkspaceMediaObject3DBackgroundColor() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaObject3DBackgroundColorSelector,
    workspaceSettingsRef
  );
}

export function useMaybeWorkspaceMediaObject3DBackgroundColor() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaObject3DBackgroundColorSelector,
    workspaceRefs?.workspaceSettingsRef
  );
}

/** Raw section-level value from view spec (not cascaded from workspace). */
export function useSectionMediaObject3DBackgroundColor() {
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeSectionMediaObject3DBackgroundColorSelector,
    sectionRef
  );
}

export function useMaybeSectionMediaObject3DBackgroundColor() {
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeSectionMediaObject3DBackgroundColorSelector,
    sectionRef
  );
}

/** Workspace → section cascaded value with default. */
export function useCascadingSectionMediaObject3DBackgroundColor() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaObject3DBackgroundColorSelector,
    workspaceSettingsRef,
    sectionRef
  );
}

/** Raw panel-level value from the panel's own config (not cascaded). */
export function usePanelMediaObject3DBackgroundColor(
  panelRef: PanelTypes.Ref | undefined
) {
  return useMemoizedSelector(
    makePanelMediaObject3DBackgroundColorSelector,
    panelRef
  );
}

export function useMaybeCascadingSectionMediaObject3DBackgroundColor() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaObject3DBackgroundColorSelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef
  );
}

/**
 * Effective object3DBackgroundColor for the media settings drawer at the given settings level.
 * Use this instead of reading `config.object3DBackgroundColor` for migrated fields.
 */
export function useMediaObject3DBackgroundColorDisplay(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
) {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeMediaObject3DBackgroundColorDisplaySelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    settingsLevel,
    panelRef
  );
}

/**
 * Whether to show the reset-to-parent button for object3DBackgroundColor at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export function useShouldShowMediaObject3DBackgroundColorResetAtLevel(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): boolean {
  const sectionObject3DBackgroundColor =
    useMaybeSectionMediaObject3DBackgroundColor();
  const panelObject3DBackgroundColor =
    usePanelMediaObject3DBackgroundColor(panelRef);

  if (settingsLevel === SettingsLevel.PANEL) {
    return panelObject3DBackgroundColor != null;
  }

  return (
    settingsLevel === SettingsLevel.SECTION &&
    sectionObject3DBackgroundColor != null
  );
}

/** Raw workspace-level value from view spec. */
export function useWorkspaceMediaDisableVideoStream() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaDisableVideoStreamSelector,
    workspaceSettingsRef
  );
}

export function useMaybeWorkspaceMediaDisableVideoStream() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaDisableVideoStreamSelector,
    workspaceRefs?.workspaceSettingsRef
  );
}

/** Raw section-level value from view spec (not cascaded from workspace). */
export function useSectionMediaDisableVideoStream() {
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeSectionMediaDisableVideoStreamSelector,
    sectionRef
  );
}

export function useMaybeSectionMediaDisableVideoStream() {
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeSectionMediaDisableVideoStreamSelector,
    sectionRef
  );
}

/** Workspace → section cascaded value with default. */
export function useCascadingSectionMediaDisableVideoStream(): boolean {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaDisableVideoStreamSelector,
    workspaceSettingsRef,
    sectionRef
  );
}

/** Raw panel-level value from the panel's own config (not cascaded). */
export function usePanelMediaDisableVideoStream(
  panelRef: PanelTypes.Ref | undefined
) {
  return useMemoizedSelector(
    makePanelMediaDisableVideoStreamSelector,
    panelRef
  );
}

export function useMaybeCascadingSectionMediaDisableVideoStream(): boolean {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaDisableVideoStreamSelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef
  );
}

/**
 * Effective disableVideoStream for the media settings drawer at the given settings level.
 * Use this instead of reading `config.disableVideoStream` for migrated fields.
 */
export function useMediaDisableVideoStreamDisplay(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): boolean {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeMediaDisableVideoStreamDisplaySelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    settingsLevel,
    panelRef
  );
}

/**
 * Whether to show the reset-to-parent button for disableVideoStream at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export function useShouldShowMediaDisableVideoStreamResetAtLevel(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): boolean {
  const sectionDisableVideoStream = useMaybeSectionMediaDisableVideoStream();
  const panelDisableVideoStream = usePanelMediaDisableVideoStream(panelRef);

  if (settingsLevel === SettingsLevel.PANEL) {
    return panelDisableVideoStream != null;
  }

  return (
    settingsLevel === SettingsLevel.SECTION && sectionDisableVideoStream != null
  );
}

/** Raw workspace-level value from view spec. */
export function useWorkspaceMediaAlwaysOriginal() {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaAlwaysOriginalSelector,
    workspaceSettingsRef
  );
}

export function useMaybeWorkspaceMediaAlwaysOriginal() {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  return useMemoizedSelector(
    makeWorkspaceMediaAlwaysOriginalSelector,
    workspaceRefs?.workspaceSettingsRef
  );
}

/** Raw section-level value from view spec (not cascaded from workspace). */
export function useSectionMediaAlwaysOriginal() {
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeSectionMediaAlwaysOriginalSelector,
    sectionRef
  );
}

export function useMaybeSectionMediaAlwaysOriginal() {
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeSectionMediaAlwaysOriginalSelector,
    sectionRef
  );
}

/** Workspace → section cascaded value for display and section-level behavior. */
export function useCascadingSectionMediaAlwaysOriginal(): boolean {
  const {workspaceSettingsRef} = useWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContext().sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaAlwaysOriginalSelector,
    workspaceSettingsRef,
    sectionRef
  );
}

/** Raw panel-level value from the panel's own config (not cascaded). */
export function usePanelMediaAlwaysOriginal(
  panelRef: PanelTypes.Ref | undefined
) {
  return useMemoizedSelector(makePanelMediaAlwaysOriginalSelector, panelRef);
}

export function useMaybeCascadingSectionMediaAlwaysOriginal(): boolean {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeCascadingSectionMediaAlwaysOriginalSelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef
  );
}

/**
 * Effective alwaysOriginal for the media settings drawer at the given settings level.
 * Use this instead of reading `config.alwaysOriginal` for migrated fields.
 */
export function useMediaAlwaysOriginalDisplay(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): boolean {
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  return useMemoizedSelector(
    makeMediaAlwaysOriginalDisplaySelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    settingsLevel,
    panelRef
  );
}

/**
 * Whether to show the reset-to-parent button for alwaysOriginal at the current
 * settings level.
 * Product decision: the reset button is never shown at the workspace
 * level, so this always returns false there.
 */
export function useShouldShowMediaAlwaysOriginalResetAtLevel(
  settingsLevel: SettingsLevel,
  panelRef?: PanelTypes.Ref
): boolean {
  const sectionAlwaysOriginal = useMaybeSectionMediaAlwaysOriginal();
  const panelAlwaysOriginal = usePanelMediaAlwaysOriginal(panelRef);

  if (settingsLevel === SettingsLevel.PANEL) {
    return panelAlwaysOriginal != null;
  }

  return (
    settingsLevel === SettingsLevel.SECTION && sectionAlwaysOriginal != null
  );
}
